// app-saccw/src/scenes/tafiraSupervision/hooks/useTafiraTour.js
import { useRef, useEffect } from "react";
import Shepherd from "shepherd.js";
import { useDispatch } from "react-redux";
import { setTourTafiraCompleted } from "../../../state";

export const tourOptions = {
  defaultStepOptions: {
    cancelIcon: { enabled: true },
    scrollTo: { behavior: "smooth", block: "center" },
    classes: "shepherd-theme-custom",
  },
  useModalOverlay: true,
};

export const buildTourSteps = (dispatch) => [
  {
    id: "welcome",
    title: "Bienvenido a Facturación de Supervisión",
    text: "Este formulario le permite generar su <strong>Aviso de Cobro</strong> por servicios de supervisión. Le guiaremos paso a paso por cada campo.",
    buttons: [
      {
        text: "Comenzar",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "doc-status",
    title: "Estado de Documentación",
    text: "Este indicador muestra si tiene avisos anteriores con documentación pendiente. Haga clic en él para ver el detalle.",
    attachTo: { element: "#tour-doc-status", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "aporte-switch",
    title: "Aporte Mensual (Opcional)",
    text: "Active este interruptor si desea incluir los conceptos del <strong>Aporte Mensual</strong>. Al activarlo aparecerán los campos <em>Monto Aporte</em> y <em>Mora Aporte</em> calculados automáticamente.",
    attachTo: { element: "#tour-aporte-switch", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "fecha-emision",
    title: "Fecha de Emisión",
    text: "Se asigna automáticamente con la fecha de hoy. No es editable.",
    attachTo: { element: "#tour-fecha-emision", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "servicio",
    title: "Tipo de Servicio",
    text: "Seleccione el servicio de supervisión que desea facturar. Este es el primer campo requerido y habilita los demás.",
    attachTo: { element: "#tour-servicio", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "anio",
    title: "Año de Facturación",
    text: "Se habilita después de seleccionar el servicio. Elija el año del período que va a reportar.",
    attachTo: { element: "#tour-anio", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "mes",
    title: "Mes de Facturación",
    text: "Se habilita después de seleccionar año. El sistema valida que no exista un aviso duplicado para ese mes y que el aviso del mes anterior esté registrado.",
    attachTo: { element: "#tour-mes", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "fecha-vencimiento",
    title: "Fecha de Vencimiento",
    text: "Calculada automáticamente como el <strong>día 10 del mes siguiente</strong> al período declarado. No es editable.",
    attachTo: { element: "#tour-fecha-vencimiento", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "fecha-pago",
    title: "Fecha de Pago",
    text: "Indica cuándo realizará el pago. Si es posterior a la fecha de vencimiento, se calculará una <strong>mora automáticamente</strong>.",
    attachTo: { element: "#tour-fecha-pago", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "ingreso-servicio",
    title: "Ingreso del Servicio",
    text: "Ingrese el total de ingresos del período. El <strong>Monto Tarifa</strong> y la mora se calculan automáticamente al ingresar este valor.",
    attachTo: { element: "#tour-ingreso-servicio", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "montos",
    title: "Montos Calculados",
    text: "La <strong>Tarifa de Supervisión</strong> (0.5% de tus ingresos) y la <strong>Mora</strong> por pago tardío son calculadas y mostradas aquí. No son editables.",
    attachTo: { element: "#tour-montos", on: "bottom" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "dropzone",
    title: "Documentación de Soporte",
    text: "Adjunte al menos un archivo <strong>PDF</strong> que respalde los ingresos declarados (ej. reporte de ingresos). Este campo es obligatorio.",
    attachTo: { element: "#tour-dropzone", on: "top" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "descripcion",
    title: "Descripción (Opcional)",
    text: "Puede agregar observaciones o notas adicionales sobre este aviso.",
    attachTo: { element: "#tour-descripcion", on: "top" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Siguiente",
        action() {
          return this.next();
        },
      },
    ],
  },
  {
    id: "submit",
    title: "Generar Aviso de Cobro",
    text: "Cuando todos los campos estén completos, haga clic aquí para generar el aviso. El PDF se descargará automáticamente.",
    attachTo: { element: "#tour-submit", on: "top" },
    buttons: [
      {
        text: "Atrás",
        action() {
          return this.back();
        },
      },
      {
        text: "Finalizar",
        action() {
          dispatch(setTourTafiraCompleted());
          this.complete();
        },
      },
    ],
  },
];

export function useTafiraTour() {
  const dispatch = useDispatch();
  const tourRef = useRef(null);

  if (!tourRef.current) {
    const t = new Shepherd.Tour(tourOptions);
    buildTourSteps(dispatch).forEach((step) => t.addStep(step));
    tourRef.current = t;
  }

  useEffect(() => {
    const t = tourRef.current;
    return () => {
      t?.cancel();
    };
  }, []);

  return tourRef.current;
}
